import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../layout/Container';
import Button from '../ui/Button';

interface HeroProps {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  imageSrc?: string;
  imageAlt?: string;
  backgroundClass?: string;
  backgroundType?: 'gradient' | 'image' | 'color';
}

/**
 * Hero section component for page headers with call-to-action
 */
const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  imageSrc,
  imageAlt,
  backgroundClass = 'bg-gradient-to-br from-primary-dark via-primary to-secondary',
  backgroundType = 'gradient',
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.7, ease: 'easeOut', delay: 0.3 },
    },
  };

  return (
    <section className={`py-12 md:py-16 ${backgroundClass} text-white overflow-hidden`}>
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            className="max-w-2xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              variants={itemVariants}
            >
              {title}
            </motion.h1>
            
            <motion.p
              className="text-lg md:text-xl mb-8 text-white/90"
              variants={itemVariants}
            >
              {subtitle}
            </motion.p>
            
            <motion.div className="flex flex-wrap gap-4" variants={itemVariants}>
              <Button
                variant="accent"
                size="lg"
                onClick={() => window.location.href = ctaLink}
                className="font-semibold shadow-lg hover:shadow-accent/30 transition-shadow"
              >
                {ctaText}
              </Button>
              
              {secondaryCtaText && secondaryCtaLink && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = secondaryCtaLink}
                  className="font-semibold border-white text-white hover:bg-white/10"
                >
                  {secondaryCtaText}
                </Button>
              )}
            </motion.div>
          </motion.div>
          
          {imageSrc && (
            <motion.div
              className="hidden lg:block"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
            >
              <img
                src={imageSrc}
                alt={imageAlt || 'Hero image'}
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </motion.div>
          )}
        </div>
      </Container>
      
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full"></div>
        <div className="absolute top-40 -left-20 w-60 h-60 bg-accent opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-secondary opacity-10 rounded-full"></div>
      </div>
    </section>
  );
};

export default Hero;